import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import ContactSupportIcon from '@material-ui/icons/ContactSupportTwoTone';
import ProductImage from '@sprinx/react-com/ProductImage';
import FormikExtended from '@sprinx/react-forms/FormikExtended';
import { TranslationDictionary } from '@sprinx/react-globalize/types';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import WrapDialogForm from '@sprinx/react-mui-components/WrapDialogForm';
import TextField from '@sprinx/react-mui-fields/TextField';
import clsx from 'clsx';
import reject from 'lodash/reject';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { ProductImage as TProductImage } from '../../../@sprinx/knihovka-types/products';
import { apiClientState, localeState } from '../../../api/appState';
import { useShowSnackbarMessage } from '../../../api/snackbars';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export interface ProductQuestionsProduct {
  id: string;
  image?: TProductImage;
  name: TranslationDictionary;
  sku: string;
}

export interface ProductDetailActionsQuestionProps extends StandardDivProps<ProductDetailActionsQuestionClassKey> {
  product: ProductQuestionsProduct;
}

export type ProductDetailActionsQuestionClassKey = 'root' | 'dialogProductWrapper' | 'dialogProductName';

const themeSettings = { name: 'ProductDetailActionsQuestion' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ProductDetailActionsQuestionClassKey, {}>({
      root: {},
      dialogProductWrapper: {
        display: 'flex',
        alignItems: 'center',
      },
      dialogProductName: {
        ...theme.typography.subtitle1,
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.primary.dark,
        marginLeft: theme.spacing(2),
      },
    }),
  themeSettings,
);

const ProductDetailActionsQuestion = React.forwardRef<HTMLDivElement, ProductDetailActionsQuestionProps>(
  ({ className, classes: pClasses, product }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const [open, setOpen] = React.useState(false);
    const t = useTranslateWithFallback();

    return (
      <>
        <div ref={ref} className={clsx(classes.root, className)}>
          <Button onClick={() => setOpen(true)} color='default' variant='outlined' startIcon={<ContactSupportIcon />}>
            {t('app/haveAQuestion')}
          </Button>
        </div>
        <ProductQuestionForm
          classes={{
            productWrapper: classes.dialogProductWrapper,
            productName: classes.dialogProductName,
          }}
          open={open}
          product={product}
          onClose={() => setOpen(false)}
        />
      </>
    );
  },
);

ProductDetailActionsQuestion.displayName = 'ProductDetailActionsQuestion';

export default withThemeProps(themeSettings)(ProductDetailActionsQuestion);

function ProductQuestionForm({
  classes,
  open,
  onClose: handleClose,
  product,
}: {
  classes: Record<'productWrapper' | 'productName', string>;
  onClose: () => void;
  open: boolean;
  product: ProductQuestionsProduct;
}) {
  const t = useTranslateWithFallback();
  const { id, name, sku } = product || {};
  const locale = useRecoilValue(localeState);
  const apiClient = useRecoilValue(apiClientState);
  const showMessage = useShowSnackbarMessage();

  const [isClicked, setIsClicked] = React.useState(false);

  const handleSave = React.useCallback(
    (data: {
      email: string;
      locale: string;
      phone: string;
      product: ProductQuestionsProduct;
      question: string;
      regNo: string;
    }): Promise<any> => {
      setIsClicked(true);
      return apiClient
        .post<
          { status: 'error' | 'success' | 'warning' },
          {
            email: string;
            locale: string;
            phone: string;
            product: ProductQuestionsProduct;
            question: string;
            regNo: string;
          }
        >('/v1/product-questions', data)
        .then((res) => {
          if ('status' in res && res.status === 'success') {
            showMessage(t('app/productQuestion/successResponse'), { variant: 'success' });
          } else {
            showMessage(t('app/productQuestion/errorResponse'), {
              variant: 'error',
            });
          }
        });
    },
    [apiClient, showMessage, t],
  );

  return open ? (
    <FormikExtended
      onSave={!isClicked ? handleSave : () => new Promise(() => reject('multiple-times'))}
      onFormReleased={(r) => {
        setIsClicked(false);
        handleClose();
        return r;
      }}
      initialValues={{
        email: '',
        locale,
        phone: '',
        question: '',
        regNo: '',
        product: { sku, name, id },
      }}
      validationFastest
      validationSchema={{
        email: { type: 'email' },
        phone: { type: 'string', empty: false },
        regNo: { type: 'string', empty: false },
        question: { type: 'string', empty: false },
      }}
      render={({ onSubmit, values, errors, onChange: handleChange, dirty }) => (
        <WrapDialogForm
          dirty={dirty}
          dialogTitle={t('productDetailPage/productQuestsion')}
          open={open}
          onSubmit={onSubmit}
          onClose={handleClose}
          // variant='flat-no-gutters'
          dialogProps={{
            buttonSaveLabel: t('common/send'),
            fullWidth: true,
            maxWidth: 'md',
          }}
          render={() => {
            return (
              <>
                <div className={classes.productWrapper}>
                  {product.image && (
                    <ProductImage alt={t(product.name, { acceptString: true })} src={product.image} size='mini' />
                  )}
                  <div className={classes.productName}>{t(product.name, { acceptString: true })}</div>
                </div>
                <TextField
                  label={t('common/yourEmail')}
                  helperText={''}
                  value={values.email}
                  error={errors.email}
                  onChange={handleChange('email')}
                  fullWidth
                />
                <TextField
                  label={t('app/productQuestion/yourPhone')}
                  helperText={''}
                  value={values.phone}
                  error={errors.phone}
                  onChange={handleChange('phone')}
                  fullWidth
                />
                <TextField
                  label={t('app/productQuestion/yourRegNo')}
                  helperText={''}
                  value={values.regNo}
                  error={errors.regNo}
                  onChange={handleChange('regNo')}
                  fullWidth
                />
                <TextField
                  multiline
                  label={t('app/productQuestion/yourQuestion')}
                  helperText={''}
                  value={values.question}
                  error={errors.question}
                  onChange={handleChange('question')}
                  fullWidth
                />
              </>
            );
          }}
        />
      )}
    />
  ) : null;
}
